import { Suspense, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Loading from "../../../../common/components/ui/loading/Loading";
import {
  IMaintenance,
  MAINTENANCE_STATUS,
  MaintenanceType,
} from "../../../../types/maintenance.type";
import {
  TableFilters,
  TableFiltersState,
} from "../../../../common/components/ui/table/TableFilters";
import GenericTable from "../../../../common/components/ui/table/GenericTable";
import TableFooter from "../../../../common/components/ui/table/TableFooter";
import { useToastContext } from "../../../../common/components/ui/toast/toast.context";
import { getMaintenance } from "../../../../common/services/maintenance.api";

import { formatDateAndTime } from "../../../../common/helpers/date.helpers";

import { IVehicle } from "../../../../types/vehicle.type";

import { AuthContext } from "../../../../common/components/auth/authContext/AuthContext";
import { useCompanyStore } from "@/common/states/store/Company";
import PendenciesMaintenance from "@/modules/maintenances/components/PendenciesMaintenance";
import ToCheckModal from "../modals/ToCheckModal";
import { HiCheckCircle, HiExclamationTriangle } from "react-icons/hi2";

const STATUS_LABELS = {
  [MAINTENANCE_STATUS.Finalize]: "Finalizado",
};

function getStatusLabels(status: MAINTENANCE_STATUS) {
  return STATUS_LABELS[status] || status;
}
const EMPTY_ACTION = { eventName: "", data: undefined };

const headers = {
  licensePlate: "Placa",
  name: "Cliente",
  waitingTicket: "Senha",
  status: "Status",
  mechanic: "Mecânico",
  typePlanRenter: "Plano do cliente",
  verificationStatus: "Verificação",
  updatedAt: "Atualizado em",
  createdAt: "Criado em",
  typeMaintenance: "Tipo de Manutenção",
};

function TableMaintenanceCompleted() {
  useContext(AuthContext);
  const navigate = useNavigate();
  const { notifyError } = useToastContext();
  const [page, setPage] = useState<number>(1);
  const [data, setData] = useState({ itemsPerPage: 10, totalItems: 0 });
  const [maintenance, setMaintenance] = useState<IMaintenance[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [action, setAction] = useState<{ eventName: string; data?: unknown }>(
    EMPTY_ACTION
  );
  const { selectedCompanies, filterMode } = useCompanyStore();

  const actions = [
    {
      label: "Verificar",
      action: (data) => setAction({ eventName: "toCheck", data: data }),
    },
  ];
  const isAction = (eventName: string) => action.eventName === eventName;
  const cleanActionData = () => {
    setAction(EMPTY_ACTION);
  };
  function getPreviousWeekRange() {
    const today = new Date();
    const dayOfWeek = today.getDay();

    const endDate = new Date(today);
    endDate.setDate(today.getDate() - dayOfWeek - 1);
    endDate.setHours(23, 59, 59, 999);

    const startDate = new Date(endDate);
    startDate.setDate(endDate.getDate() - 6);
    startDate.setHours(0, 0, 0, 0);

    return { startDate, endDate };
  }
  const { startDate, endDate } = getPreviousWeekRange();
  const [filters, setFilters] = useState<TableFiltersState>({
    search: "",
    status: [MAINTENANCE_STATUS.Finalize],
    filterColumn: "",
    orderBy: "-updatedAt",
    startDate: startDate,
    endDate: endDate,
  });
  useEffect(() => {
    async function loadMaintenance(filters, page) {
      setIsLoading(true);
      const result = await getMaintenance({
        ...filters,
        page,
        status: MAINTENANCE_STATUS.Finalize,
        startDate: startDate,
        endDate: endDate,
      });
      setData({
        itemsPerPage: data.itemsPerPage,
        totalItems: result.totalItems,
      });
      if (!result.ok) {
        notifyError(
          (result as { message?: string })?.message || "unknown error"
        );
        notifyError(
          (result as { message?: string })?.message || "unknown error"
        );
        return;
      }
      const sortedData = (result.data as IMaintenance[]).sort((a, b) => {
        if (a.waitingTicket && b.waitingTicket) {
          return a.waitingTicket - b.waitingTicket;
        }
        return 0;
      });
      setMaintenance(sortedData);
      setIsLoading(false);
    }
    loadMaintenance(filters, page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, page, selectedCompanies, filterMode]);

  return (
    <Suspense fallback={<Loading />}>
      <div className="table-section">
        <TableFilters
          dashboardHeader="Manutenções cadastradas"
          placeholder="Digite o nome do cliente, a placa, o mecânico, o chassi ou a senha."
          statusOptions={STATUS_LABELS}
          onSearch={setFilters}
          entity="maintenance"
          tableData={maintenance}
          hideStatusFilter={true}
          initialStartDate={startDate}
          initialEndDate={endDate}
        />

        <GenericTable
          headers={headers}
          data={maintenance}
          actions={actions}
          setFilters={({ orderBy }) => {
            setFilters((prevFilters) => ({
              ...prevFilters,
              orderBy,
            }));
          }}
          renders={
            {
              licensePlate: (licensePlate, vehicle) => (
                <a
                  className="text-blue-600 hover:text-bluePrimary cursor-pointer"
                  onClick={() =>
                    navigate(`/veiculos/${(vehicle as IVehicle).licensePlate}`)
                  }
                  target="_blank"
                >
                  {licensePlate}
                </a>
              ),
              waitingTicket: (waitingTicket) => waitingTicket || "-",
              status: getStatusLabels,
              mechanic: (mechanic) => mechanic || "-",
              pendencies: (_, maintenance) => (
                <PendenciesMaintenance
                  maintenance={maintenance as MaintenanceType}
                />
              ),
              verificationStatus: (_, maintenance) => {
                const chargeInfo = (maintenance as IMaintenance)
                  .chargeMaintenance;

                if (chargeInfo?.contested) {
                  return (
                    <div className="flex justify-center" title="Contestado">
                      <HiExclamationTriangle className="h-6 w-6 text-yellow-500" />
                    </div>
                  );
                }
                if (chargeInfo?.confirmed) {
                  return (
                    <div className="flex justify-center" title="Confirmado">
                      <HiCheckCircle className="h-6 w-6 text-green-500" />
                    </div>
                  );
                }
                return null;
              },
              updatedAt: formatDateAndTime,
              createdAt: formatDateAndTime,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } as any
          }
          entity="maintenance"
        />
        {isLoading && <Loading />}

        <TableFooter
          page={page || 1}
          totalItems={data.totalItems || 0}
          itemsPerPage={+(filters.itemsPerPage ?? 10)}
          goToPage={setPage}
        />
      </div>
      {isAction("toCheck") && (
        <ToCheckModal
          onClosed={cleanActionData}
          licensePlate={(action.data as IMaintenance)?.licensePlate}
          maintenanceId={(action.data as IMaintenance)?._id}
          chargeStatus={(action.data as IMaintenance)?.chargeMaintenance}
        />
      )}
    </Suspense>
  );
}

export default TableMaintenanceCompleted;
